import React, { useEffect, useMemo, useRef, useState } from 'react';

// --- SVG Icons ---
const ChatIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const SendIcon = ({ busy, disabled }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: disabled ? 0.5 : 1 }}
  >
    {busy ? (
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3" >
         <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
      </circle>
    ) : (
      <path
        d="M22 2L11 13"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
    <path
      d="M22 2L15 22L11 13L2 9L22 2Z"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Main Component ---
export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(''); // Used for chat message OR email
  const [nameInput, setNameInput] = useState(''); // NEW: Specifically for Name
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [isWaitingForLead, setIsWaitingForLead] = useState(false);
  
  const bottomRef = useRef(null);
  const hasAutoOpened = useRef(false);
  const inputRef = useRef(null);
  const originalQuestionRef = useRef(null);

  // Update this URL if your API is on a different port/domain in production
  const apiBase = useMemo(() => 'https://benignfasciculationsyndrome.org', []);

  // Helper: Validates email format
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Effect: Check disclaimer status
  useEffect(() => {
    const accepted = localStorage.getItem('disclaimerAccepted') === 'true';
    if (accepted) {
      setDisclaimerAccepted(true);
    }
  }, []);

  // Effect: Initialize session / Load history
  useEffect(() => {
    const initializeSession = async () => {
      const storedSessionId = localStorage.getItem('chatSessionId');
      if (storedSessionId) {
        try {
          const res = await fetch(`${apiBase}/api/chat/history?sessionId=${storedSessionId}`);
          if (res.ok) {
            const data = await res.json();
            setMessages(data.messages || []);
            setSessionId(storedSessionId);
          } else {
             localStorage.removeItem('chatSessionId');
          }
        } catch (err) {
            console.error("Failed to fetch history:", err);
        }
      }
    };
    initializeSession();
  }, [apiBase]);

  // Effect: Auto-open chat after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (hasAutoOpened.current || open) return;
      
      if (messages.length === 0) {
        setMessages([{ role: 'assistant', content: "Hello! Is there anything I can help with today?" }]);
      }
      setOpen(true);
      hasAutoOpened.current = true;
    }, 3000);

    return () => clearTimeout(timer);
  }, [open, messages.length]);

  // Effect: Auto-scroll to bottom
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if(bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [messages, open]);

  // Effect: Show disclaimer modal
  useEffect(() => {
    if (open && !disclaimerAccepted) {
      setTimeout(() => {
        setShowDisclaimer(true);
      }, 200);
    }
  }, [open, disclaimerAccepted]);

  // --- API Handling ---
  async function streamResponse(payload) {
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // Handle the 403 "Hard Gate"
      if (res.status === 403) {
        const data = await res.json(); 
        
        if (data.sessionId) {
            setSessionId(data.sessionId);
            localStorage.setItem('chatSessionId', data.sessionId);
        }
        
        setIsWaitingForLead(true);
        // Show the message explaining why we stopped
        setMessages(prev => [...prev.slice(0, -1), { role: 'assistant', content: "To protect our community and provide the best support, please provide your name and email to continue." }]);
        setBusy(false);
        return; // Stop here, wait for user input
      }

      if (!res.ok || !res.body) throw new Error('No stream body');
      
      // Update session ID if header is present
      const newSessionId = res.headers.get('X-Session-Id');
      if (newSessionId && newSessionId !== sessionId) {
          setSessionId(newSessionId);
          localStorage.setItem('chatSessionId', newSessionId);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        const chunk = decoder.decode(value || new Uint8Array(), { stream: !done });
        if (chunk) {
          setMessages(prev => {
            const copy = [...prev];
            const i = copy.length - 1;
            if (i >= 0 && copy[i].role === 'assistant') {
              copy[i] = { ...copy[i], content: copy[i].content + chunk };
            }
            return copy;
          });
        }
      }
    } catch (err) {
      console.error(err);
      setError('Sorry—something went wrong. Please try again.');
      setMessages(prev => [...prev.slice(0, -1), { role: 'assistant', content: 'Sorry—something went wrong. Please try again.' }]);
    } finally {
      setBusy(false);
    }
  }

  async function handleSendMessage(e) {
    if (e) e.preventDefault();
    const text = input.trim(); // "input" is Email when waiting for lead, "message" otherwise
    
    if (isWaitingForLead) {
        // --- Lead Capture Logic ---
        // Validate both Name and Email
        if (!nameInput.trim() || !isValidEmail(text)) {
            setError("Please provide both your name and a valid email.");
            return;
        }

        setBusy(true);
        setError(''); 
        try {
            const leadRes = await fetch(`${apiBase}/api/leads/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: nameInput.trim(), 
                    email: text,
                    sessionId,
                })
            });

            if (!leadRes.ok) throw new Error('Failed to save lead');
            
            // Success! Resume chat
            setIsWaitingForLead(false);
            setInput('');
            setNameInput('');
            
            // Add placeholder for the answer
            setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
            
            // Resend the original question
            const payload = { 
                sessionId, 
                messages: [...messages, {role: 'user', content: originalQuestionRef.current}], 
                continue: true 
            };
            streamResponse(payload);

        } catch (err) {
            console.error(err);
            setError('Could not verify details. Please try again.');
            setBusy(false);
        }
        return;
    }

    // --- Standard Chat Logic ---
    if (!text || busy || !disclaimerAccepted) return;

    originalQuestionRef.current = text; 
    setMessages(prev => [...prev, { role: 'user', content: text }]); 
    setInput('');
    
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
    
    const payload = { sessionId, messages: [...messages, {role: 'user', content: text}] };
    streamResponse(payload);
  }

  function handleAcceptDisclaimer() {
    localStorage.setItem('disclaimerAccepted', 'true');
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        style={{...styles.fab, ...(open ? styles.fabOpen : {})}}
        aria-label="Open BFS chat"
        title={open ? 'Close chat' : 'Open chat'}
      >
        {open ? <CloseIcon /> : <ChatIcon />}
      </button>

      {open && (
        <div style={styles.panel} role="dialog" aria-label="BFS Support">
          <div style={styles.header}>
              <div style={styles.headerTop}>
                <div style={styles.headerTitle}>BFS Assistant</div>
              </div>
              <div style={styles.headerSubtitle}>
                Educational only — not medical advice
              </div>
          </div>

          <div style={styles.messagesContainer}>
            <div style={styles.messages} aria-live="polite">
                {messages.map((m, i) => (
                <div
                    key={i}
                    style={{
                    ...styles.bubble,
                    ...(m.role === 'user' ? styles.userBubble : styles.assistantBubble)
                    }}
                >
                    {m.content}
                </div>
                ))}
                {busy && messages[messages.length-1]?.role === 'assistant' && messages[messages.length-1]?.content === '' &&
                  <div style={{...styles.bubble, ...styles.assistantBubble, ...styles.typingBubble}}>
                    <span style={{...styles.typingDot, animationDelay:'0s'}}></span>
                    <span style={{...styles.typingDot, animationDelay:'0.2s'}}></span>
                    <span style={{...styles.typingDot, animationDelay:'0.4s'}}></span>
                  </div>
                }
                <div ref={bottomRef} />
            </div>
          </div>
          
          {error && <div style={styles.error}>{error}</div>}

          {/* Conditional Rendering: Lead Form vs Chat Input */}
          {isWaitingForLead ? (
             <div style={styles.leadFormContainer}>
               <div style={styles.leadText}>Enter your details to continue the live chat:</div>
               <div style={styles.leadFields}>
                 {/* Name Input */}
                 <input 
                   type="text" 
                   value={nameInput}
                   onChange={e => setNameInput(e.target.value)}
                   placeholder="Your Name"
                   style={styles.input}
                   disabled={busy}
                 />
                 {/* Email Input */}
                 <input
                   type="email"
                   value={input}
                   onChange={e => setInput(e.target.value)}
                   placeholder="email@example.com"
                   style={styles.input}
                   disabled={busy}
                 />
               </div>
               <button 
                 onClick={handleSendMessage} 
                 disabled={!isValidEmail(input) || !nameInput.trim() || busy}
                 style={{...styles.unlockButton}}
               >
                  {busy ? 'Verifying...' : 'Submit'}
               </button>
             </div>
          ) : (
            <form style={styles.inputRow} onSubmit={handleSendMessage}>
                <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Type your question…"
                style={styles.input}
                disabled={busy}
                aria-label="Message input"
                />
                <button type="submit" style={{...styles.send, ...( (busy || !input.trim()) ? styles.sendDisabled : {})}} disabled={busy || !input.trim() || !disclaimerAccepted}>
                <SendIcon busy={busy} disabled={busy || !input.trim()} />
                </button>
            </form>
          )}

          {showDisclaimer && (
            <div style={styles.disclaimerOverlay}>
                <div style={styles.disclaimerModal}>
                    <div style={styles.headerTitle}>Important Disclaimer</div>
                    <div style={styles.disclaimerText}>
                        This chatbot is for informational and educational purposes only. The information provided is not a substitute for professional medical advice, diagnosis, or treatment. 
                        <br/><br/>
                        <strong>This is not medical advice.</strong>
                        <br/><br/>
                        Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition. Never disregard professional medical advice or delay in seeking it because of something you have read from this chatbot.
                    </div>
                    <button onClick={handleAcceptDisclaimer} style={styles.disclaimerButton}>
                        I Understand and Agree
                    </button>
                </div>
            </div>
          )}
        </div>
      )}
      <style>{`
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes typing {
            0%, 60%, 100% { transform: translateY(0); }
            30% { transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
}

const BRAND_COLOR = '#3b82f6';

const styles = {
  fab: {
    position: 'fixed',
    right: 20,
    bottom: 20,
    zIndex: 9999,
    width: 60,
    height: 60,
    borderRadius: '50%',
    border: 'none',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    background: BRAND_COLOR,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s ease-in-out, background-color 0.2s',
  },
  fabOpen: {
      transform: 'scale(0.9)',
      backgroundColor: '#9ca3af'
  },
  panel: {
    position: 'fixed',
    right: 20,
    bottom: 95,
    width: 'min(90vw, 380px)',
    maxHeight: 'min(75vh, 600px)',
    background: '#f9fafb',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9999,
    border: '1px solid #e5e7eb',
    animation: 'fadeIn 0.3s ease-out'
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fff',
    flexShrink: 0,
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: '#1f2937'
  },
  headerSubtitle: {
    marginTop: 4,
    fontSize: 12,
    color: '#6b7280'
  },
  error: {
    padding: '8px 20px',
    fontSize: 13,
    color: '#b91c1c',
    background: '#fee2e2',
    textAlign: 'center'
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    position: 'relative',
  },
  messages: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  bubble: {
    padding: '12px 16px',
    borderRadius: 18,
    lineHeight: 1.5,
    fontSize: 14,
    maxWidth: '85%',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  userBubble: {
    alignSelf: 'flex-end',
    background: BRAND_COLOR,
    color: '#fff',
    borderRadius: '18px 18px 4px 18px',
  },
  assistantBubble: {
    alignSelf: 'flex-start',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    color: '#374151',
    borderRadius: '18px 18px 18px 4px',
  },
  typingBubble: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '16px 20px',
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#9ca3af',
    animation: 'typing 1.2s infinite ease-in-out',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderTop: '1px solid #e5e7eb',
    background: '#fff'
  },
  leadFormContainer: {
    padding: 15, 
    background: '#eff6ff', 
    borderTop: '1px solid #3b82f6',
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  leadText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1e40af',
    marginBottom: 4
  },
  leadFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: 12,
    border: '1px solid #d1d5db',
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  unlockButton: {
    width: '100%', 
    padding: '10px', 
    background: BRAND_COLOR, 
    color: '#fff', 
    border: 'none', 
    borderRadius: 8, 
    cursor: 'pointer',
    fontWeight: '600',
    marginTop: 5
  },
  send: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    background: BRAND_COLOR,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s, opacity 0.2s',
    flexShrink: 0,
  },
  sendDisabled: {
      backgroundColor: '#a5b4fc',
      cursor: 'not-allowed',
  },
  disclaimerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 20,
  },
  disclaimerModal: {
    background: '#fff',
    padding: 24,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    maxWidth: '90%',
    width: '400px',
  },
  disclaimerText: {
    marginTop: 12,
    fontSize: 13,
    color: '#4b5563',
    lineHeight: 1.6,
    maxHeight: '40vh',
    overflowY: 'auto'
  },
  disclaimerButton: {
    marginTop: 20,
    padding: '12px 20px',
    borderRadius: 12,
    border: 'none',
    background: BRAND_COLOR,
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
    cursor: 'pointer',
    width: '100%',
    transition: 'background-color 0.2s',
  },
};